// src/components/ShortabilityBadge.jsx
//
// Small chip showing whether a stock can be shorted on Alpaca.
// Renders inline next to each symbol in a symbol picker / dropdown.
//
// States:
//   'easy_to_borrow' (ETB)  — green, "Easy to Borrow"
//   'hard_to_borrow' (HTB)  — amber, "Hard to Borrow" (still shortable, higher cost)
//   'not_shortable'         — red,   "Not Shortable" (no short allowed)
//   null / undefined        — gray,  "Loading..."   (not yet fetched)
//
// Usage:
//   <ShortabilityBadge status={shortability[symbol]} />
//
// Data comes from useShortability hook (batch-fetches when symbol set
// changes, debounced).

import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';
import { Block, Warning, CheckCircle, HelpOutline } from '@mui/icons-material';


export function ShortabilityBadge({ status, compact = false }) {
  // Unknown / not yet loaded — don't show anything (avoid jitter as user types)
  if (status === undefined) return null;

  const config = (() => {
    switch (status) {
      case 'easy_to_borrow':
        return {
          label: compact ? 'ETB' : 'Easy to Borrow',
          icon: <CheckCircle sx={{ fontSize: 12 }} />,
          bg: '#052e16',
          fg: '#46b88a',
          border: '#065f46',
          tooltip: 'Easy to Borrow — available for shorting at standard rates.',
        };
      case 'hard_to_borrow':
        return {
          label: compact ? 'HTB' : 'Hard to Borrow',
          icon: <Warning sx={{ fontSize: 12 }} />,
          bg: '#451a03',
          fg: '#d9a441',
          border: '#92400e',
          tooltip: 'Hard to Borrow — shortable, but borrow fees may apply. Check Alpaca for current rate.',
        };
      case 'not_shortable':
        return {
          label: compact ? 'NSS' : 'Not Shortable',
          icon: <Block sx={{ fontSize: 12 }} />,
          bg: '#2a0a0a',
          fg: '#d97b72',
          border: '#991b1b',
          tooltip: 'Not Shortable — no shares available to borrow. Bot can only go long on this symbol.',
        };
      case 'loading':
        return {
          label: '...',
          icon: <HelpOutline sx={{ fontSize: 12 }} />,
          bg: '#0f1218',
          fg: '#666',
          border: 'rgba(255,255,255,0.06)',
          tooltip: 'Checking shortability...',
        };
      default:
        return null;
    }
  })();

  if (!config) return null;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="caption" fontWeight={600}>{config.label}</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
            {config.tooltip}
          </Typography>
        </Box>
      }
      arrow
    >
      <Chip
        size="small"
        icon={config.icon}
        label={config.label}
        sx={{
          height: compact ? 18 : 20,
          fontSize: '0.6rem',
          fontWeight: 600,
          bgcolor: config.bg,
          color: config.fg,
          border: `1px solid ${config.border}`,
          '& .MuiChip-icon': { color: config.fg, marginLeft: '4px' },
        }}
      />
    </Tooltip>
  );
}
